import { useCallback, useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Frame,
  Loader2,
} from "lucide-react";
import { Button } from "../button";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../tooltip";
import { cn } from "../../../lib/utils";

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

export interface PdfViewerProps {
  src: string;
  className?: string;
}

const ZOOM_STEP = 0.2;
const MAX_ZOOM = 4;
const MIN_ZOOM = 0.4;

/**
 * PDF half of the file-viewer family, rendered with pdf.js — the browser
 * iframe fallback depended on the browser's built-in viewer, which is exactly
 * what mobile webviews and locked-down builds don't ship. One page on a
 * canvas at a time (devicePixelRatio-aware), with page and zoom controls
 * matching the rest of the family's toolbar idiom.
 */
export function PdfViewer({ src, className }: PdfViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // RenderTask for the in-flight paint — a newer render must cancel the older
  // one or two draws race onto the same canvas.
  const renderTaskRef = useRef<ReturnType<
    pdfjs.PDFPageProxy["render"]
  > | null>(null);

  const [doc, setDoc] = useState<pdfjs.PDFDocumentProxy | null>(null);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [fitWidth, setFitWidth] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Container width, in CSS px — the fit-width scale is derived from it, so a
  // resize has to re-render just like a zoom does.
  const [containerWidth, setContainerWidth] = useState(0);

  // Load the document. Destroying the *task* (not the proxy) is what tears
  // down the worker fetches — and a stale task outliving the viewer would
  // otherwise leak them.
  useEffect(() => {
    let cancelled = false;
    const task = pdfjs.getDocument({ url: src });
    setLoading(true);
    setError(false);
    setDoc(null);
    setPage(1);
    setZoom(1);
    setFitWidth(true);
    task.promise
      .then((loaded) => {
        if (cancelled) return;
        setDoc(loaded);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
      void task.destroy();
    };
  }, [src]);

  // Track container width for fit-width.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) setContainerWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const renderPage = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!doc || !canvas || containerWidth === 0) return;
    try {
      const pdfPage = await doc.getPage(page);
      const base = await pdfPage.getViewport({ scale: 1 });
      // Fit-width derives the base scale from the container; zoom multiplies
      // either that or the page's natural 1:1.
      const fitScale = containerWidth / base.width;
      const scale = (fitWidth ? fitScale : 1) * zoom;
      const dpr = window.devicePixelRatio || 1;
      const viewport = pdfPage.getViewport({ scale: scale * dpr });

      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      canvas.style.width = `${Math.floor(viewport.width / dpr)}px`;
      canvas.style.height = `${Math.floor(viewport.height / dpr)}px`;

      renderTaskRef.current?.cancel();
      const task = pdfPage.render({
        canvasContext: canvas.getContext("2d")!,
        canvas,
        viewport,
      });
      renderTaskRef.current = task;
      await task.promise;
    } catch (err) {
      // A cancelled render throws RenderingCancelledException — that is the
      // happy path of a newer render superseding this one, not a failure.
      if ((err as { name?: string })?.name === "RenderingCancelledException")
        return;
      setError(true);
    }
  }, [doc, page, zoom, fitWidth, containerWidth]);

  useEffect(() => {
    void renderPage();
  }, [renderPage]);

  // Re-render at the same page when fit-width re-engages.
  useEffect(() => {
    if (fitWidth) setZoom(1);
  }, [fitWidth]);

  const goPage = useCallback(
    (next: number) => {
      if (!doc) return;
      setPage(Math.min(Math.max(next, 1), doc.numPages));
    },
    [doc],
  );

  return (
    <div className={cn("flex h-full flex-col", className)}>
      <TooltipProvider>
        {/* Toolbar — same rhythm as ImageViewer's: centred clusters, hairline
            separators, tooltips on everything. */}
        <div className="flex shrink-0 items-center justify-center gap-1 py-2 px-4 bg-sunken">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => goPage(page - 1)}
                disabled={!doc || page <= 1}
                className="h-8 w-8 p-0"
                aria-label="Previous page"
              >
                <ChevronLeft size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Previous page</TooltipContent>
          </Tooltip>

          <span className="w-20 text-center text-xs font-medium tabular-nums text-slate-500 dark:text-slate-400">
            {doc ? `${page} / ${doc.numPages}` : "– / –"}
          </span>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => goPage(page + 1)}
                disabled={!doc || page >= (doc?.numPages ?? 1)}
                className="h-8 w-8 p-0"
                aria-label="Next page"
              >
                <ChevronRight size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Next page</TooltipContent>
          </Tooltip>

          <div className="mx-1 h-4 w-px bg-slate-200 dark:bg-slate-700" />

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFitWidth(false);
                  setZoom((z) => Math.max(z - ZOOM_STEP, MIN_ZOOM));
                }}
                disabled={!doc || (!fitWidth && zoom <= MIN_ZOOM)}
                className="h-8 w-8 p-0"
                aria-label="Zoom out"
              >
                <ZoomOut size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Zoom out</TooltipContent>
          </Tooltip>

          <span className="w-12 text-center text-xs font-medium tabular-nums text-slate-500 dark:text-slate-400">
            {Math.round(zoom * 100)}%
          </span>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFitWidth(false);
                  setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM));
                }}
                disabled={!doc || (!fitWidth && zoom >= MAX_ZOOM)}
                className="h-8 w-8 p-0"
                aria-label="Zoom in"
              >
                <ZoomIn size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Zoom in</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFitWidth((f) => !f)}
                className="h-8 w-8 p-0"
                aria-label={fitWidth ? "Actual size" : "Fit to width"}
              >
                <Frame size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {fitWidth ? "Actual size" : "Fit to width"}
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>

      {/* Page well */}
      <div
        ref={containerRef}
        className="flex-1 min-h-0 overflow-auto bg-slate-100 dark:bg-slate-800/30 p-4"
      >
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <span
              role="status"
              aria-label="Loading document"
              className="flex items-center gap-2 text-xs text-slate-400"
            >
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Loading…
            </span>
          </div>
        ) : error ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-500 dark:text-slate-400">
            Couldn't render this PDF.
          </div>
        ) : (
          <div className="flex min-h-full items-start justify-center">
            <canvas
              ref={canvasRef}
              className="bg-white shadow-md"
              aria-label="PDF page preview"
              role="img"
            />
          </div>
        )}
      </div>
    </div>
  );
}
